import type { Draft, Reducer } from "@reduxjs/toolkit";
import { createReducer, current, original } from "@reduxjs/toolkit";
import { isEqual } from "es-toolkit";

import { assert } from "../../asserts/index.ts";
import { nodeActionSchema, type NodeAction, type NodeDesignsRevision } from "../../schemas/index.ts";
import {
  nodeDesignsReducerCreate,
  removeLanguageAction,
  updateNodeConfigAction,
  type NodeDesignsReducer,
} from "../reducer/index.ts";
import type { NodeDesignsState } from "../types.ts";
import { getRedoRevision, getUndoRevision } from "../undoredo/index.ts";
import { contentLanguagesReducer } from "./contentLanguages/index.ts";
import {
  flushAction,
  loadAction,
  redoAction,
  revertAction,
  sendServerActionCompleted,
  sendServerActionInProgressAction,
  undoAction,
  type NautilusStateAction,
} from "./nautilusActions.ts";
import { nautilusDesignerInitialState, type NautilusDesignerState, type NautilusServerState } from "./nautilusState.ts";
import { applyRevisionServerAction, redoRevisionServerAction, undoRevisionServerAction } from "./serverActions.ts";
import { computeRevisionChecksum } from "./shared.ts";

/*

    do optimistic updates ourselves
    which means keep a snapshot before submitting revision to server and revert to it if server says now

    do revision batching with reducer queuing of actions and middleware to checkpoint
    use middleware to submit revisions and revert on error
    use middleware to submit undo/redo and revert on error

    include immediately applied node designs state (no waiting for batching)
    add interim state on top of that (not node designs state and per node based on manifest spec)
    add node transient state (separate and reset on revert and undo/redo; ideally only when that node or descendents changed)
    */
export interface NautilusStateReducerOptions<AppConfig extends Record<string, unknown>> {
  nodeDesignsReducer: NodeDesignsReducer;
  appConfigReducer: Reducer<AppConfig>;
}

export const nautilusStateReducerCreate = <AppConfig extends Record<string, unknown>>({
  nodeDesignsReducer,
  appConfigReducer,
}: NautilusStateReducerOptions<AppConfig>): Reducer<
  NautilusDesignerState<AppConfig>,
  NautilusStateAction,
  NautilusDesignerState<AppConfig>
> => {
  const nonNotifyingNodeDesignsReducer = nodeDesignsReducerCreate();

  const getAppliedRevisions = (input: Pick<NautilusServerState, "nodeDesigns" | "revision" | "unappliedRevisions">) => {
    let nodeDesigns = structuredClone(input.nodeDesigns);

    const revisionsToApply = input.unappliedRevisions.filter((r) => !r.skip);
    for (const revision of revisionsToApply) {
      nodeDesigns = revision.actions.reduce(nonNotifyingNodeDesignsReducer, nodeDesigns);
    }
    const revision = revisionsToApply[revisionsToApply.length - 1]?.revision ?? input.revision;
    return { nodeDesigns, revision } as const;
  };

  const applyRevisions = (
    draft: Draft<Pick<NautilusServerState, "nodeDesigns" | "revision" | "unappliedRevisions">>,
    revisionsToApplyCount: number,
  ) => {
    let newNodeDesigns = original(draft.nodeDesigns) as NodeDesignsState;
    // NOTE: splice will return the revisions to apply AND remove them from unappliedRevisions
    const revisionsToApply = draft.unappliedRevisions.splice(0, revisionsToApplyCount).filter((r) => !r.skip);
    for (const revision of revisionsToApply) {
      newNodeDesigns = revision.actions.reduce(nonNotifyingNodeDesignsReducer, newNodeDesigns);
    }
    const revision = revisionsToApply[revisionsToApply.length - 1]?.revision ?? draft.revision;
    assert(newNodeDesigns);
    // @ts-ignore excessively deep types error
    draft.nodeDesigns = newNodeDesigns;
    draft.revision = revision;
  };

  return createReducer(nautilusDesignerInitialState as NautilusDesignerState<AppConfig>, (builder) => {
    builder.addCase(loadAction, (_, action) => {
      const { nodeDesigns, revision, unappliedRevisions, maxUnappliedRevisions, languages, appConfig } = action.payload;

      return {
        appConfig: appConfig as AppConfig,
        nodeDesigns: getAppliedRevisions({ nodeDesigns, revision, unappliedRevisions }).nodeDesigns,
        appliedRevisions: [...unappliedRevisions],
        pendingRevisionActions: [],
        server: {
          nodeDesigns,
          revision,
          unappliedRevisions,
          unsentServerActions: [],
        },
        contentLanguages: [...languages],
        settings: {
          maxUnappliedRevisions,
        },
      };
    });

    builder.addCase(flushAction, (state) => {
      const {
        server: { unsentServerActions, revision },
        pendingRevisionActions,
        appliedRevisions,
      } = state;

      const lastRevision = appliedRevisions[appliedRevisions.length - 1]?.revision ?? revision ?? 0;

      const dedupedActions = [...dedupeActions(pendingRevisionActions)];
      const newRevision: NodeDesignsRevision = {
        revision: lastRevision + 1,
        actions: dedupedActions,
        createdOn: new Date(),
        checksum: computeRevisionChecksum(dedupedActions),
      };
      pendingRevisionActions.length = 0;
      // same as server, if we add a revision we can't undo and want to redo this if undo so clear out old undos
      state.appliedRevisions = [...appliedRevisions.filter((r) => !r.skip), newRevision];
      unsentServerActions.push(applyRevisionServerAction(newRevision));
    });

    builder.addCase(undoAction, (state) => {
      const { server, appliedRevisions, pendingRevisionActions } = state;
      assert(appliedRevisions.length > 0, `No revisions to undo.`);
      assert(pendingRevisionActions.length === 0, `Pending revision actions should be empty before undoing.`);

      const undoRevision = getUndoRevision(appliedRevisions);
      assert(undoRevision, `All revisions already undone.`);
      undoRevision.skip = true;

      const serverClone = structuredClone({
        revision: server.revision,
        nodeDesigns: original(server.nodeDesigns),
        unappliedRevisions: current(appliedRevisions),
      }) as Pick<NautilusServerState, "nodeDesigns" | "revision" | "unappliedRevisions">;

      state.nodeDesigns = getAppliedRevisions(serverClone).nodeDesigns;
      server.unsentServerActions.push(undoRevisionServerAction({ revision: undoRevision.revision }));
    });

    builder.addCase(redoAction, (state) => {
      const { server, appliedRevisions, pendingRevisionActions } = state;
      assert(appliedRevisions.length > 0, `No revisions to redo.`);
      assert(pendingRevisionActions.length === 0, `Pending revision actions should be empty before redoing.`);

      const redoRevision = getRedoRevision(appliedRevisions);
      assert(redoRevision?.skip, `No undone revisions to redo.`);
      delete redoRevision.skip;

      const serverClone = structuredClone({
        revision: server.revision,
        nodeDesigns: original(server.nodeDesigns),
        unappliedRevisions: current(appliedRevisions),
      }) as Pick<NautilusServerState, "nodeDesigns" | "revision" | "unappliedRevisions">;

      state.nodeDesigns = getAppliedRevisions(serverClone).nodeDesigns;
      server.unsentServerActions.push(redoRevisionServerAction({ revision: redoRevision.revision }));
    });

    builder.addCase(sendServerActionInProgressAction, (state, { payload: sendInProgress }) => {
      const { action: serverAction } = sendInProgress;
      assert(
        isEqual(serverAction, state.server.unsentServerActions[0]),
        `Server action ${JSON.stringify(serverAction)} expected to be first unsent server action but got ${JSON.stringify(state.server.unsentServerActions[0])}.`,
      );
      assert(!state.server.sendInProgress, `Action delivery to server already in progress.`);
      state.server.sendInProgress = sendInProgress;
    });

    builder.addCase(revertAction, (state) => {
      const {
        server: { nodeDesigns, unappliedRevisions, revision },
      } = state;

      const serverClone = structuredClone({
        revision,
        nodeDesigns: original(nodeDesigns),
        unappliedRevisions: original(unappliedRevisions),
      }) as Pick<NautilusServerState, "nodeDesigns" | "revision" | "unappliedRevisions">;

      state.nodeDesigns = getAppliedRevisions(serverClone).nodeDesigns; // revert to the last successfully sent revision
      state.pendingRevisionActions.length = 0;
      state.appliedRevisions = [...serverClone.unappliedRevisions];
      state.server.unsentServerActions.length = 0;
      state.server.sendInProgress = undefined;
    });

    builder.addCase(sendServerActionCompleted, (state, { payload }) => {
      const {
        server,
        settings: { maxUnappliedRevisions },
      } = state;
      assert(
        server.unsentServerActions.findIndex((a) => isEqual(a, payload)) === 0,
        `Server actions sent in wrong order or not found. Expected type ${payload.type} with ${JSON.stringify(payload.payload)}.`,
      );
      assert(server.sendInProgress, `No server action delivery in progress.`);
      assert(
        isEqual(server.sendInProgress.action, payload),
        `Server Action ${JSON.stringify(payload)} send not in progress.`,
      );
      const sentAction = server.unsentServerActions[0];
      switch (payload.type) {
        case "APPLY_REVISION": {
          assert(sentAction.type === "APPLY_REVISION", `Expected sent action to be APPLY_REVISION.`);
          const sentRevision = sentAction.payload;

          server.unappliedRevisions.push(sentRevision);

          if (sentRevision.actions.some((action) => removeLanguageAction.match(action))) {
            // the server deletes the content language row in the same revision and that cannot be reversed,
            // so fold everything into the snapshot to put the revision out of undo's reach
            applyRevisions(server, server.unappliedRevisions.length);
          } else if (server.unappliedRevisions.length > maxUnappliedRevisions) {
            // apply the first unapplied revision (sliding window as happens on server)
            applyRevisions(server, 1);
          }

          // remove first applied revision since it is now applied on server and can't be undone
          const minServerRevision = Math.min(...server.unappliedRevisions.map((r) => r.revision));
          state.appliedRevisions = state.appliedRevisions.filter((r) => r.revision >= minServerRevision);

          const [removedAction] = server.unsentServerActions.splice(0, 1);
          assert(isEqual(removedAction, sentAction), `Expected to remove sent action from unsent server actions.`);
          break;
        }

        case "UNDO_REVISION": {
          const {
            payload: { revision },
          } = payload;

          // note that undo should never be sent before the revision is sent to the server
          const undoRevision = server.unappliedRevisions.find((r) => r.revision === revision);
          assert(undoRevision, `Undo revision ${revision} not found in server unapplied revisions.`);

          // flag the revision as undone
          undoRevision.skip = true;

          const [removedAction] = server.unsentServerActions.splice(0, 1);
          assert(isEqual(removedAction, sentAction), `Expected to remove sent action from unsent server actions.`);
          break;
        }

        case "REDO_REVISION": {
          const {
            payload: { revision },
          } = payload;

          // note that redo should never be sent before the revision is sent to the server
          const redoRevision = server.unappliedRevisions.find((r) => r.revision === revision);
          assert(redoRevision, `Redo revision ${revision} not found in server unapplied revisions.`);

          // flag the revision as not skipped
          delete redoRevision.skip;

          const [removedAction] = server.unsentServerActions.splice(0, 1);
          assert(isEqual(removedAction, sentAction), `Expected to remove sent action from unsent server actions.`);
          break;
        }
        default:
          // @ts-expect-error exhaustiveness check
          assert(false, `Unknown server action type ${payload.type} sent.`);
      }
      server.sendInProgress = undefined;
    });

    builder.addDefaultCase((draft, inputAction) => {
      const { data: action, success } = nodeActionSchema.safeParse(inputAction);
      if (success && action) {
        // REMOVE_LANGUAGE is a node action that also changes the content language list
        draft.contentLanguages = contentLanguagesReducer(draft.contentLanguages, action);
        // default immediate state
        nodeDesignsReducer(draft.nodeDesigns, action);
        draft.pendingRevisionActions.push(action);
      } else {
        appConfigReducer(draft.appConfig as AppConfig, inputAction);
        draft.contentLanguages = contentLanguagesReducer(draft.contentLanguages, inputAction);
      }
    });
  });
};

// retains only the last action that has an UPDATE_NODE_CONFIG type action on the same node id
const dedupeActions = (actions: NodeAction[]): NodeAction[] => {
  const lastIndexOfUpdateConfigByNodeId = new Map<number, number>();
  for (const [index, action] of actions.entries()) {
    if (updateNodeConfigAction.match(action)) {
      const { id } = action.payload;
      lastIndexOfUpdateConfigByNodeId.set(id, index);
    }
  }

  const dedupedActions = actions.filter((action, index) => {
    if (updateNodeConfigAction.match(action)) {
      const { id } = action.payload;
      return lastIndexOfUpdateConfigByNodeId.get(id) === index;
    }
    return true;
  });

  return dedupedActions;
};
